<script setup lang="ts">
import AppHeaderNavLink from '@/modules/App/AppHeaderNavLink.vue'
import { usePermissionsStore } from '@/modules/IdentityAndAccess/permissionsStore'
import Export from '@/modules/Project/Export.vue'
import { useEntitySidebarNavigation } from '@/modules/Project/Navigation/useEntitySidebarNavigation'
import ProjectPermissionsDialog from '@/modules/Project/Permissions/ProjectPermissionsDialog.vue'
import { useRootProjectId } from '@/modules/Project/Permissions/useRootProjectId'
import { useRouteTrace } from '@/modules/Project/routing/useRouteTrace'
import { useProject } from '@/modules/Project/useProject'
import { useProperty } from '@/modules/Project/useProperty'
import { useResolveEntityRoute } from '@/modules/Project/useResolveEntityRoute'
import { useResolveProjectRoute } from '@/modules/Project/useResolveProjectRoute'
import NextWorkspaceProjectCrumbs from '@/modules/Workspaces/Crumbs/NextWorkspaceProjectCrumbs.vue'
import AppHeader from '@/sharedComponents/AppHeader.vue'
import SidebarToggle from '@/sharedComponents/SidebarToggle.vue'
import { useRouteParams } from '@/sharedComposables/useRouteParams'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { FeatureFlag } from '../App/featureFlags'
import { useFeatureFlags } from '../App/useFeatureFlags'
import AgentPermissionsDialog from '../Project/Permissions/AgentPermissionsDialog.vue'

const props = defineProps<{
  workspaceId: string
  projectId: string
  entityId?: string
  parentProjectId?: string
}>()

const permissionsStore = usePermissionsStore()
const canManageProjectPermissions = computed(
  () =>
    permissionsStore.currentProjectPermissions.invite_members ||
    permissionsStore.currentProjectPermissions.add_members ||
    permissionsStore.currentProjectPermissions.remove_members ||
    permissionsStore.currentProjectPermissions.update_members,
)

const rootProjectId = useRootProjectId()

const { parentEntityId, parentViewId, viewId } = useRouteParams()
const resolveProjectTableRoute = useResolveProjectRoute()
const resolveEntityRoute = useResolveEntityRoute()
const { getSerializedTrace } = useRouteTrace()

const projectStore = useProject()

const route = useRoute()

const firstActiveViewEntity = computed(() => {
  if (!projectStore.activeView) return undefined
  // quick and dirty. If there is an entityId in params,
  // and we don't want to show the sidebar toggle
  if (route.params.entityId) return undefined
  return projectStore.activeView?.entities?.[0]
})

const getEntityRoute = () => {
  if (!projectStore.activeView) return undefined
  const entityId = props.entityId || firstActiveViewEntity.value?.id
  if (entityId) {
    return resolveEntityRoute({
      workspaceId: props.workspaceId,
      projectId: props.projectId,
      entityId,
      parentEntityId: parentEntityId.value,
      parentProjectId: props.parentProjectId,
      parentViewId: parentViewId.value,
    })
  }

  return {
    name: 'WorkspaceProjectEntityViewEmpty',
    params: {
      workspaceId: props.workspaceId,
      projectId: props.projectId,
    },
    query: { parentViewId: parentViewId.value, trace: getSerializedTrace() },
  }
}

const navRoutes = computed(() => {
  const workspaceId = props.workspaceId
  const projectId = props.projectId

  return {
    main: resolveProjectTableRoute({
      parentEntityId: parentEntityId.value,
      parentProjectId: props.parentProjectId,
      parentViewId: parentViewId.value,
      projectId,
      viewId: viewId.value,
      workspaceId,
      trace: 'same',
    }),
    workflow: projectStore.activeView
      ? { name: 'WorkflowView', params: { workspaceId, projectId } }
      : undefined,
    entity: getEntityRoute(),
  } as const
})

const { isSidebarOpen, openSidebar: openEntitySidebar, closeSidebar } = useEntitySidebarNavigation()
const openSidebarForFirstEntity = () => {
  if (firstActiveViewEntity.value) {
    openEntitySidebar(firstActiveViewEntity.value.id)
  }
}

const propertyStore = useProperty()
const isAnySidebarOpen = computed(() => isSidebarOpen.value || propertyStore.sidebarIsOpen)

const closeAllSidebars = () => {
  closeSidebar()
  propertyStore.sidebarIsOpen = false
}

const isFoldersEnabled = useFeatureFlags(FeatureFlag.AGENT_FOLDERS)
</script>

<template>
  <AppHeader>
    <template #crumbs>
      <NextWorkspaceProjectCrumbs />
    </template>

    <template #default>
      <div class="flex w-[258px] justify-center gap-1">
        <AppHeaderNavLink
          class="w-1/3"
          :to="navRoutes.main"
        >
          Build
        </AppHeaderNavLink>
        <AppHeaderNavLink
          v-if="navRoutes.entity"
          class="w-1/3"
          :to="navRoutes.entity"
          :exact="false"
        >
          Review
        </AppHeaderNavLink>
        <AppHeaderNavLink
          v-if="navRoutes.workflow"
          class="w-1/3"
          :to="navRoutes.workflow"
        >
          Automate
        </AppHeaderNavLink>
      </div>
    </template>

    <template #actions>
      <Export
        :workspace-id="workspaceId"
        :project-id="projectId"
      />
      <DarwinButton
        v-if="projectId && canManageProjectPermissions"
        size="sm"
        rounded
        variant="black"
        @click="permissionsStore.dialogOpen = true"
      >
        Share
      </DarwinButton>
      <AgentPermissionsDialog
        v-if="isFoldersEnabled"
        :open="permissionsStore.dialogOpen"
        :root-project-id="rootProjectId || ''"
        :workspace-id="workspaceId"
        @close="permissionsStore.dialogOpen = false"
      />
      <ProjectPermissionsDialog
        v-else
        :open="permissionsStore.dialogOpen"
        :root-project-id="rootProjectId || ''"
        :workspace-id="workspaceId"
        @close="permissionsStore.dialogOpen = false"
      />
      <SidebarToggle
        v-if="firstActiveViewEntity || isAnySidebarOpen"
        :is-open="isSidebarOpen"
        side="right"
        open-tooltip="Show entity details"
        close-tooltip="Close details"
        @toggle="isAnySidebarOpen ? closeAllSidebars() : openSidebarForFirstEntity()"
      />
    </template>
  </AppHeader>
</template>
